/**
 * Sprint 23.8 — Workforce health sidebar block (block 2).
 *
 * Monthly learnings, weekly retros, weekly AI spend (+ vs-budget when a budget
 * is set). Reads the batched overview's `workforce_health`.
 */

import { type CSSProperties } from "react";
import { SidebarBlock } from "../shell/SidebarBlock";
import { useWorkspaceOverview } from "../../hooks/use-workspace-overview";
import { formatMoney } from "./format";

export function WorkforceHealthBlock() {
  const { data } = useWorkspaceOverview();
  const health = data?.workforce_health;

  // vs budget — only when a budget is configured (null in v1).
  const budget = health?.ai_spend_budget ?? null;
  const pct =
    budget && budget > 0 && health
      ? Math.round((health.ai_spend_this_week / budget) * 100)
      : null;

  return (
    <SidebarBlock title="Workforce health">
      <div style={rows}>
        <Row label="Learnings / month" value={health ? String(health.learnings_this_month) : "—"} />
        <Row label="Retros / week" value={health ? String(health.retros_this_week) : "—"} />
        <Row label="AI spend / week" value={health ? formatMoney(health.ai_spend_this_week) : "—"} />
        {pct !== null && (
          <Row
            label="vs budget"
            value={`${pct}%`}
            valueColor={pct <= 100 ? "var(--color-text-success)" : "var(--color-text-warning)"}
          />
        )}
      </div>
    </SidebarBlock>
  );
}

function Row({ label, value, valueColor }: { label: string; value: string; valueColor?: string }) {
  return (
    <div style={row}>
      <span style={rowLabel}>{label}</span>
      <span style={{ ...rowValue, ...(valueColor ? { color: valueColor } : {}) }}>{value}</span>
    </div>
  );
}

const rows: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 5,
  fontSize: 12,
};

const row: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: 8,
};

const rowLabel: CSSProperties = {
  color: "var(--color-text-tertiary)",
};

const rowValue: CSSProperties = {
  color: "var(--color-text-primary)",
};
